interface PullToRefreshIndicatorProps {
  /** Pull distance in px from the top. */
  pullDistance: number;
  /** Refresh in flight. */
  refreshing: boolean;
  /** Distance at which the gesture commits to refresh. */
  triggerPx: number;
}

/**
 * Pull-to-refresh indicator — iOS-style circular spinner.
 *
 * 2026-05-05 v2: simplified for bulletproof rendering. No transform
 * tricks (previous version's rotate(-90deg) on SVG element was flaky on
 * iOS Safari standalone — could clip / disappear). Uses a Lucide-style
 * RefreshCw icon that fades in as the user pulls and spins when refresh
 * is in flight.
 *
 * Outer wrapper unchanged from the working text-overlay baseline (#115)
 * so the gesture detection in usePullToRefresh hook remains untouched.
 */
export function PullToRefreshIndicator({
  pullDistance,
  refreshing,
  triggerPx,
}: PullToRefreshIndicatorProps) {
  // Progress 0→1 from pullDistance / triggerPx, clamped.
  const progress = Math.min(1, Math.max(0, pullDistance / triggerPx));
  const ready = progress >= 1;

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-x-0 top-0 flex justify-center overflow-hidden"
      style={{
        height: pullDistance,
        transition: pullDistance === 0 ? 'height 200ms' : 'none',
      }}
    >
      <div className="flex items-end pb-2">
        <div
          className={[
            'flex h-7 w-7 items-center justify-center rounded-full bg-white shadow-md dark:bg-[#1E293B]',
            refreshing ? 'animate-spin' : '',
          ].join(' ')}
          style={{
            opacity: refreshing ? 1 : Math.max(0.3, progress),
            transform: refreshing ? undefined : `scale(${0.7 + 0.3 * progress})`,
            transition: refreshing
              ? 'none'
              : 'opacity 120ms, transform 120ms',
          }}
        >
          {/* RefreshCw — Lucide icon. Visible on white/dark bg via currentColor. */}
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke={ready ? '#2563EB' : '#64748B'}
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ transition: 'stroke 120ms' }}
          >
            <path d="M3 12a9 9 0 0 1 15.5-6.36L21 8" />
            <path d="M21 3v5h-5" />
            <path d="M21 12a9 9 0 0 1-15.5 6.36L3 16" />
            <path d="M3 21v-5h5" />
          </svg>
        </div>
      </div>
    </div>
  );
}
